import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { Search, Plus, Minus, Trash2, Receipt, Barcode, Camera, WifiOff } from "lucide-react";
import { api, inputCls, labelCls, rd, NCF_TYPES, PAYMENT_METHODS, askPin, invoiceWhatsAppUrl, beep, printThermal, CATEGORIES, GENDERS } from "./posShared";
import { enqueue } from "./offlineQueue";
import CameraScanner from "./CameraScanner";

export default function Facturar({ products, customers, settings, reload, reloadCustomers, onInvoice, user, prefill, onPrefillDone }) {
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [fromQuote, setFromQuote] = useState(null);
  const [scanState, setScanState] = useState(null);
  const [camera, setCamera] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [discount, setDiscount] = useState(0);
  const [discountCode, setDiscountCode] = useState("");
  const [appliedCode, setAppliedCode] = useState(null);
  const [checkingCode, setCheckingCode] = useState(false);
  const [applyItbis, setApplyItbis] = useState(false);
  const [fiscal, setFiscal] = useState(false);
  const [ncfType, setNcfType] = useState("consumo");
  const [method, setMethod] = useState("efectivo");
  const [paid, setPaid] = useState(0);
  const [dueDate, setDueDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [custSearch, setCustSearch] = useState("");
  const [custOpen, setCustOpen] = useState(false);
  const [quickAdd, setQuickAdd] = useState(null);
  const [newCust, setNewCust] = useState(null);
  const [genderF, setGenderF] = useState("all");
  const [newProduct, setNewProduct] = useState(null);
  const [suggest, setSuggest] = useState(null);
  const [address, setAddress] = useState("");
  const [deliveryLabel, setDeliveryLabel] = useState("");
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  // El comprobante fiscal (NCF) obliga a aplicar ITBIS.
  useEffect(() => { if (fiscal) setApplyItbis(true); }, [fiscal]);

  // Convertir cotización -> factura: precarga carrito, cliente y dirección.
  useEffect(() => {
    if (!prefill) return;
    const items = (prefill.items || []).map((i) => {
      const p = products.find((x) => x.id === i.product_id);
      return { product_id: i.product_id, name: i.name, price: i.price, qty: i.qty, max: p ? p.qty : 0 };
    });
    const sinStock = items.filter((i) => i.max <= 0).map((i) => i.name);
    setCart(items);
    if (prefill.customer_id) setCustomerId(prefill.customer_id);
    if (prefill.customer_address) setAddress(prefill.customer_address);
    if (prefill.apply_itbis) setApplyItbis(true);
    setFromQuote(prefill.id || null);
    toast.info(`Cotización ${prefill.number} cargada. Completa el pago para facturar.`);
    if (sinStock.length) toast.warning(`Sin stock disponible: ${sinStock.join(", ")}. Repón el inventario antes de facturar.`);
    onPrefillDone && onPrefillDone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const itbisRate = Number(settings?.itbis_rate ?? 18);
  const inStock = products.filter((p) => p.qty > 0);
  const filtered = inStock.filter(
    (p) =>
      (genderF === "all" || (p.gender || "unisex") === genderF) &&
      (p.name.toLowerCase().includes(q.toLowerCase()) ||
        (p.code || "").toLowerCase().includes(q.toLowerCase()) ||
        (p.barcode || "").toLowerCase().includes(q.toLowerCase()))
  );
  const GENDER_TABS = [
    { key: "all", label: "Todos" },
    { key: "mujer", label: "Damas" },
    { key: "hombre", label: "Caballeros" },
    { key: "unisex", label: "Unisex" },
  ];

  // Barcode scanner acts as a keyboard ending in Enter: find exact match and add to cart.
  const flash = (s) => { setScanState(s); setTimeout(() => setScanState(null), 700); };
  const handleCode = (raw) => {
    const codeRaw = String(raw || "").trim();
    const code = codeRaw.toLowerCase();
    if (!code) return;
    const match = inStock.find(
      (p) => (p.barcode || "").toLowerCase() === code || (p.code || "").toLowerCase() === code
    );
    const target = match || (filtered.length === 1 ? filtered[0] : null);
    if (target) { add(target); setQ(""); beep(true); flash("ok"); toast.success(`Agregado: ${target.name}`); return; }
    // ¿Existe pero sin stock?
    const existsAny = products.find(
      (p) => (p.barcode || "").toLowerCase() === code || (p.code || "").toLowerCase() === code
    );
    beep(false); flash("err");
    if (existsAny) { toast.error(`${existsAny.name} está sin stock`); return; }
    // ¿Se parece a otro producto ya registrado? Sugerirlo antes de crear uno nuevo.
    const suggestions = products.filter((p) => codeResembles(codeRaw, p)).slice(0, 6);
    if (suggestions.length) { setSuggest({ code: codeRaw, matches: suggestions }); return; }
    // No registrado y sin parecidos: si es admin y parece un código escaneado, ofrecer alta rápida.
    const looksLikeCode = /\d/.test(codeRaw) && !/\s/.test(codeRaw) && codeRaw.length >= 4;
    if (isAdmin && looksLikeCode) {
      setNewProduct({ name: "", code: codeRaw, barcode: codeRaw, category: "miniperfume", gender: "unisex", price: "", cost: "", qty: "" });
      toast.info(`Código ${codeRaw} no registrado. Créalo para agregarlo.`);
    } else {
      toast.error("Producto no encontrado o sin stock");
    }
  };

  const useSuggested = (p) => {
    setSuggest(null); setQ("");
    if (p.qty > 0) { add(p); beep(true); flash("ok"); toast.success(`Agregado: ${p.name}`); }
    else toast.error(`${p.name} está sin stock`);
  };
  const createFromSuggest = (code) => {
    setSuggest(null);
    setNewProduct({ name: "", code, barcode: code, category: "miniperfume", gender: "unisex", price: "", cost: "", qty: "" });
  };

  const saveNewProduct = async (d) => {
    if (!d.name.trim()) return toast.error("El nombre es obligatorio");
    try {
      const { data } = await api.post("/pos/products", {
        name: d.name, code: d.code || "", barcode: d.barcode || "",
        category: d.category, gender: d.gender,
        price: Number(d.price) || 0, cost: Number(d.cost) || 0, qty: Number(d.qty) || 0,
      });
      setNewProduct(null);
      reload();
      if (data.qty > 0) { add(data); toast.success(`${data.name} creado y agregado`); }
      else toast.success(`${data.name} creado (sin stock: ajústalo en Inventario)`);
      setQ("");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo crear el producto"); }
  };
  const onScan = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    handleCode(q);
  };

  const add = (p) => {
    setCart((c) => {
      const ex = c.find((i) => i.product_id === p.id);
      if (ex) {
        if (ex.qty >= p.qty) { toast.error("No hay más stock"); return c; }
        return c.map((i) => (i.product_id === p.id ? { ...i, qty: i.qty + 1 } : i));
      }
      return [...c, { product_id: p.id, name: p.name, price: p.price, qty: 1, max: p.qty }];
    });
  };
  const chg = (id, d) => setCart((c) => c.map((i) => i.product_id === id ? { ...i, qty: Math.max(1, Math.min(i.max, i.qty + d)) } : i));
  const rm = (id) => setCart((c) => c.filter((i) => i.product_id !== id));

  const applyDiscountCode = async () => {
    const code = discountCode.trim().toUpperCase();
    if (!code) return;
    setCheckingCode(true);
    try {
      const { data } = await api.get("/pos/otc/validate", { params: { code, category: "descuento" } });
      if (data.valid) { setAppliedCode({ code, percent: Number(data.percent) }); toast.success(`Código aplicado: ${data.percent}% de descuento`); }
      else { setAppliedCode(null); toast.error("Código inválido, ya usado o vencido"); }
    } catch (e) { toast.error("No se pudo validar el código (¿sin conexión?)"); }
    finally { setCheckingCode(false); }
  };
  const removeDiscountCode = () => { setAppliedCode(null); setDiscountCode(""); };

  const subtotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.qty, 0), [cart]);
  const disc = appliedCode ? Math.round(subtotal * Number(appliedCode.percent)) / 100 : 0;
  const base = Math.max(0, subtotal - disc);
  const itbis = (applyItbis || fiscal) ? Math.round(base * itbisRate) / 100 : 0;
  const deliveryOptions = (settings?.delivery_options || []).filter((o) => o && o.label);
  const deliveryFee = Number(deliveryOptions.find((o) => o.label === deliveryLabel)?.price || 0);
  const total = Math.round((base + itbis + deliveryFee) * 100) / 100;
  const change = ["efectivo", "combinado"].includes(method) ? Math.max(0, (Number(paid) || 0) - total) : 0;
  const balance = Math.max(0, total - (Number(paid) || 0));

  const selectedCust = customers.find((c) => c.id === customerId) || (newCust && newCust.id === customerId ? newCust : null);
  const custFiltered = customers.filter((c) => {
    const s = custSearch.trim().toLowerCase();
    if (!s) return true;
    return (
      (c.name || "").toLowerCase().includes(s) ||
      (c.phone || "").toLowerCase().includes(s) ||
      (c.whatsapp || "").toLowerCase().includes(s)
    );
  }).slice(0, 30);
  const pickCustomer = (id) => {
    setCustomerId(id); setCustSearch(""); setCustOpen(false);
    const c = customers.find((x) => x.id === id) || (newCust && newCust.id === id ? newCust : null);
    setAddress(c?.address || "");
  };

  const saveNewCustomer = async (d) => {
    if (!d.name.trim()) return toast.error("El nombre es obligatorio");
    try {
      if (d.id) {
        const { data } = await api.put(`/pos/customers/${d.id}`, d);
        setNewCust(data);
        setCustomerId(data.id);
        setAddress(data.address || "");
        setQuickAdd(null);
        reloadCustomers && reloadCustomers();
        toast.success(`Ficha de ${data.name} actualizada`);
        return;
      }
      const { data } = await api.post("/pos/customers", d);
      setNewCust(data);
      setCustomerId(data.id);
      setAddress(data.address || "");
      setCustSearch(""); setCustOpen(false); setQuickAdd(null);
      reloadCustomers && reloadCustomers();
      toast.success(`Cliente ${data.name} registrado`);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar el cliente"); }
  };

  const save = async () => {
    if (!cart.length) return toast.error("Agrega productos");
    if (fiscal && !navigator.onLine) return toast.error("No se puede emitir factura con NCF sin conexión. Desactiva el comprobante fiscal o conéctate a internet.");
    if (deliveryFee > 0 && !(address || "").trim()) return toast.error("El delivery requiere dirección. Selecciona un cliente con dirección o regístrala en su ficha (+ Nuevo cliente).");
    const cust = selectedCust;
    const body = {
      customer_id: customerId || null,
      customer_name: cust ? cust.name : "Cliente Final",
      customer_address: (address || "").trim(),
      delivery_fee: deliveryFee, delivery_label: deliveryLabel,
      discount_code: appliedCode ? appliedCode.code : "",
      items: cart.map((i) => ({ product_id: i.product_id, qty: i.qty, price: i.price })),
      apply_itbis: applyItbis || fiscal, fiscal, ncf_type: fiscal ? ncfType : null,
      payment_method: method, paid_amount: Number(paid) || 0, credit_due_date: dueDate,
    };
    if (!navigator.onLine) { saveOffline(body, cust); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/pos/invoices", body);
      toast.success(`Factura ${data.number} creada`);
      if (fromQuote) {
        try { await api.put(`/pos/quotes/${fromQuote}/status`, { status: "facturada" }); } catch (e) { console.warn("No se pudo marcar la cotización como facturada:", e); }
        setFromQuote(null);
      }
      await autoSend(data, cust);
      resetForm();
      reload();
      onInvoice(data);
    } catch (e) {
      if (!e.response) { saveOffline(body, cust); return; }
      toast.error(e.response?.data?.detail || "Error al facturar");
    } finally { setSaving(false); }
  };

  const resetForm = () => {
    setCart([]); setDiscount(0); setAppliedCode(null); setDiscountCode(""); setPaid(0); setFiscal(false); setApplyItbis(false); setCustomerId(""); setAddress(""); setDeliveryLabel("");
  };

  // No internet: queue the invoice and print a provisional ticket. It syncs on reconnect.
  const saveOffline = (body, cust) => {
    body.offline_origin = true;
    const items = cart.map((i) => ({ name: i.name, qty: i.qty, price: i.price }));
    const number = enqueue(body, {
      customer_name: cust ? cust.name : "Cliente Final",
      total, seller: user?.username || user?.email || "",
    });
    printThermal({
      number, _provisional: true, status: "pendiente_sync",
      customer_name: cust ? cust.name : "Cliente Final", customer_address: (address || "").trim(), items,
      subtotal, discount: disc, itbis, itbis_rate: itbisRate, delivery_fee: deliveryFee, delivery_label: deliveryLabel, total,
      payment_method: method, change, balance, created_at: new Date().toISOString(),
      seller: user?.username || user?.email || "",
    }, settings || {});
    toast.success(`Guardada sin conexión (${number}). Se sincronizará al reconectar.`);
    resetForm();
  };

  const autoSend = async (invoice, cust) => {
    if (!cust) return;
    if (settings?.auto_send_email && cust.email) {
      try {
        await api.post(`/pos/invoices/${invoice.id}/email`, { to: cust.email });
        invoice._sentEmail = cust.email;
        toast.success(`Factura enviada a ${cust.email}`);
      } catch (e) { toast.error("No se pudo enviar el correo automático"); }
    }
    if (settings?.auto_send_whatsapp && (cust.whatsapp || cust.phone)) {
      const url = invoiceWhatsAppUrl(invoice, settings, cust.whatsapp || cust.phone);
      const win = window.open(url, "_blank");
      invoice._sentWhatsApp = cust.whatsapp || cust.phone;
      if (!win) toast("Ventana de WhatsApp bloqueada", { action: { label: "Abrir", onClick: () => window.open(url, "_blank") } });
    }
  };

  return (
    <div>
      <h1 className="font-serif text-3xl mb-6">Facturar</h1>
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Products */}
        <div className="bg-white border border-neutral-200 rounded-xl p-4">
          <div className="relative mb-3">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input data-testid="pos-fact-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onScan} placeholder="Buscar o escanear código de barras..." className={`${inputCls} pl-9 pr-20 transition-all ${scanState === "ok" ? "!border-emerald-400 ring-2 ring-emerald-200" : scanState === "err" ? "!border-red-400 ring-2 ring-red-200" : ""}`} />
            <button data-testid="pos-fact-camera" onClick={() => setCamera(true)} title="Escanear con la cámara" className="absolute right-9 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-rose"><Camera size={17} /></button>
            <Barcode size={16} className={`absolute right-3 top-1/2 -translate-y-1/2 ${scanState === "err" ? "text-red-500" : scanState === "ok" ? "text-emerald-500" : "text-rose"}`} />
          </div>
          <p className="text-[11px] text-neutral-400 mb-2 -mt-1">Escanea con el lector o toca la cámara para agregar directo a la factura.</p>
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="pos-fact-gender-tabs">
            {GENDER_TABS.map((g) => (
              <button key={g.key} data-testid={`pos-fact-gender-${g.key}`} onClick={() => setGenderF(g.key)}
                className={`px-3.5 py-1.5 rounded-full text-[11px] uppercase tracking-[0.1em] border transition ${genderF === g.key ? "bg-espresso text-cream border-espresso" : "border-neutral-200 text-neutral-500 hover:border-espresso"}`}>
                {g.label}
              </button>
            ))}
          </div>
          <div className="space-y-1.5 max-h-[460px] overflow-y-auto">
            {filtered.map((p) => (
              <button key={p.id} data-testid={`pos-fact-add-${p.id}`} onClick={() => add(p)}
                className="w-full flex items-center justify-between text-left px-3 py-2.5 rounded-lg hover:bg-rose/5 border border-transparent hover:border-rose/20 transition">
                <div>
                  <div className="text-sm font-medium text-espresso">{p.name}</div>
                  <div className="text-xs text-neutral-400">{p.code} · stock {p.qty}</div>
                </div>
                <span className="text-sm font-medium">{rd(p.price)}</span>
              </button>
            ))}
            {filtered.length === 0 && <p className="text-neutral-400 text-sm py-6 text-center">Sin productos disponibles.</p>}
          </div>
        </div>

        {/* Cart / checkout */}
        <div className="bg-white border border-neutral-200 rounded-xl p-4 flex flex-col">
          <div><label className={labelCls}>Cliente</label>
            {selectedCust ? (
              <>
              <div data-testid="pos-fact-customer-selected" className="flex items-center justify-between gap-2 bg-rose/5 border border-rose/20 rounded-lg px-3 py-2.5">
                <div className="text-sm">
                  <div className="font-medium text-espresso">{selectedCust.name}</div>
                  {(selectedCust.phone || selectedCust.whatsapp) && <div className="text-xs text-neutral-400">{selectedCust.phone || selectedCust.whatsapp}</div>}
                </div>
                <button data-testid="pos-fact-customer-change" onClick={() => { pickCustomer(""); setCustOpen(false); }} className="text-xs text-rose hover:underline">Cambiar</button>
              </div>
              {(!selectedCust.email || !(selectedCust.whatsapp || selectedCust.phone)) && (
                <div data-testid="pos-fact-customer-missing" className="mt-2 flex items-start justify-between gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-amber-700">
                    Este cliente no tiene {[!selectedCust.email && "correo", !(selectedCust.whatsapp || selectedCust.phone) && "WhatsApp"].filter(Boolean).join(" ni ")}. Complétalo para enviarle la factura.
                  </p>
                  <button data-testid="pos-fact-customer-complete" onClick={() => setQuickAdd({ id: selectedCust.id, name: selectedCust.name || "", phone: selectedCust.phone || "", whatsapp: selectedCust.whatsapp || "", email: selectedCust.email || "", doc: selectedCust.doc || "", address: selectedCust.address || "", notes: selectedCust.notes || "" })}
                    className="shrink-0 text-xs font-medium text-amber-800 underline hover:no-underline">Completar</button>
                </div>
              )}
              </>
            ) : (
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input data-testid="pos-fact-customer-search" value={custSearch}
                  onChange={(e) => { setCustSearch(e.target.value); setCustOpen(true); }}
                  onFocus={() => setCustOpen(true)}
                  onBlur={() => setTimeout(() => setCustOpen(false), 150)}
                  placeholder="Buscar cliente por nombre o teléfono..." className={`${inputCls} pl-9`} />
                {custOpen && (
                  <div data-testid="pos-fact-customer-dropdown" className="absolute z-30 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    <button data-testid="pos-fact-customer-final" onMouseDown={(e) => e.preventDefault()} onClick={() => pickCustomer("")}
                      className="w-full text-left px-3 py-2.5 text-sm hover:bg-rose/5 border-b border-neutral-100 flex items-center justify-between">
                      <span className="font-medium text-espresso">Cliente Final</span>
                      <span className="text-[11px] text-neutral-400">sin registrar</span>
                    </button>
                    {custFiltered.map((c) => (
                      <button key={c.id} data-testid={`pos-fact-customer-option-${c.id}`} onMouseDown={(e) => e.preventDefault()} onClick={() => pickCustomer(c.id)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-rose/5 border-b border-neutral-50 last:border-0">
                        <div className="font-medium text-espresso">{c.name}</div>
                        {(c.phone || c.whatsapp) && <div className="text-xs text-neutral-400">{c.phone || c.whatsapp}</div>}
                      </button>
                    ))}
                    {custFiltered.length === 0 && <div className="px-3 py-3 text-xs text-neutral-400">Sin clientes que coincidan.</div>}
                    <button data-testid="pos-fact-customer-new" onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setQuickAdd({ name: custSearch.replace(/^\d+$/, "").trim(), phone: /^\d+$/.test(custSearch.trim()) ? custSearch.trim() : "", whatsapp: "", email: "", doc: "", address: "", notes: "" })}
                      className="w-full text-left px-3 py-2.5 text-sm text-rose hover:bg-rose/5 border-t border-neutral-100 flex items-center gap-2 sticky bottom-0 bg-white">
                      <Plus size={14} /> Nuevo cliente
                    </button>
                  </div>
                )}
              </div>
            )}
            {!selectedCust && <p className="text-[11px] text-neutral-400 mt-1">Facturando como <strong>Cliente Final</strong>. Busca arriba para asignar un cliente registrado.</p>}
          </div>

          <div className="my-3 space-y-2 max-h-[220px] overflow-y-auto">
            {cart.map((i) => (
              <div key={i.product_id} data-testid={`pos-cart-${i.product_id}`} className="flex items-center gap-2 text-sm">
                <div className="flex-1">
                  <div className="font-medium text-espresso">{i.name}</div>
                  <div className="text-xs text-neutral-400">{rd(i.price)} c/u</div>
                </div>
                <div className="flex items-center gap-1 border border-neutral-200 rounded-full px-1.5 py-1">
                  <button onClick={() => chg(i.product_id, -1)}><Minus size={12} /></button>
                  <span className="w-6 text-center">{i.qty}</span>
                  <button onClick={() => chg(i.product_id, 1)}><Plus size={12} /></button>
                </div>
                <span className="w-20 text-right font-medium">{rd(i.price * i.qty)}</span>
                <button onClick={() => rm(i.product_id)} className="text-neutral-300 hover:text-red-500"><Trash2 size={14} /></button>
              </div>
            ))}
            {cart.length === 0 && <p className="text-neutral-400 text-sm py-6 text-center">Agrega productos a la factura.</p>}
          </div>

          <div className="border-t border-neutral-100 pt-3 space-y-2">
            <div><label className={labelCls}>Código de descuento <span className="text-neutral-400 normal-case">(opcional)</span></label>
              {appliedCode ? (
                <div data-testid="pos-fact-discount-applied" className="flex items-center justify-between gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5">
                  <span className="text-sm text-emerald-700"><strong className="font-mono">{appliedCode.code}</strong> · {appliedCode.percent}% de descuento</span>
                  <button data-testid="pos-fact-discount-remove" onClick={removeDiscountCode} className="text-xs text-rose hover:underline">Quitar</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input data-testid="pos-fact-discount-code" value={discountCode} onChange={(e) => setDiscountCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyDiscountCode())} placeholder="Ingresa el código generado por el admin" className={`${inputCls} font-mono`} />
                  <button data-testid="pos-fact-discount-apply" onClick={applyDiscountCode} disabled={checkingCode || !discountCode.trim()} className="shrink-0 bg-espresso text-cream rounded-lg px-4 text-xs uppercase tracking-[0.1em] hover:bg-rose transition disabled:opacity-50">{checkingCode ? "..." : "Aplicar"}</button>
                </div>
              )}
            </div>
            <div><label className={labelCls}>Método de pago</label>
              <select data-testid="pos-fact-method" value={method} onChange={(e) => setMethod(e.target.value)} className={inputCls}>
                {PAYMENT_METHODS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm"><input data-testid="pos-fact-itbis" type="checkbox" checked={applyItbis || fiscal} disabled={fiscal} onChange={(e) => setApplyItbis(e.target.checked)} /> Aplicar ITBIS ({itbisRate}%){fiscal && <span className="text-[11px] text-neutral-400">(obligatorio con NCF)</span>}</label>
            {deliveryOptions.length > 0 && (
              <div><label className={labelCls}>Delivery <span className="text-neutral-400 normal-case">(opcional, se suma después del ITBIS)</span></label>
                <select data-testid="pos-fact-delivery" value={deliveryLabel} onChange={(e) => setDeliveryLabel(e.target.value)} className={inputCls}>
                  <option value="">Sin delivery</option>
                  {deliveryOptions.map((o) => <option key={o.label} value={o.label}>{o.label} — {rd(Number(o.price) || 0)}</option>)}
                </select>
              </div>
            )}
            {(user?.role === "admin" || user?.can_issue_fiscal) && (
              <label className="flex items-center gap-2 text-sm"><input data-testid="pos-fact-fiscal" type="checkbox" checked={fiscal} onChange={(e) => setFiscal(e.target.checked)} /> Factura con comprobante fiscal (NCF)</label>
            )}
            {fiscal && (
              <select data-testid="pos-fact-ncf-type" value={ncfType} onChange={(e) => setNcfType(e.target.value)} className={inputCls}>
                {NCF_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            )}
            {["efectivo", "tarjeta", "transferencia", "link_pago", "credito", "combinado"].includes(method) && (
              <div><label className={labelCls}>Monto pagado</label><input data-testid="pos-fact-paid" type="number" value={paid} onChange={(e) => setPaid(e.target.value)} className={inputCls} /></div>
            )}
            {method === "credito" && (
              <div><label className={labelCls}>Fecha de pago acordada</label><input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls} /></div>
            )}

            <div className="text-sm space-y-1 pt-2">
              <Row l="Subtotal" v={rd(subtotal)} />
              {disc > 0 && <Row l={`Descuento${appliedCode ? ` (${appliedCode.percent}%)` : ""}`} v={`- ${rd(disc)}`} />}
              {applyItbis && <Row l={`ITBIS ${itbisRate}%`} v={rd(itbis)} />}
              {deliveryFee > 0 && <Row l={`Delivery${deliveryLabel ? ` · ${deliveryLabel}` : ""}`} v={rd(deliveryFee)} />}
              <div className="flex justify-between text-lg font-serif pt-1"><span>Total</span><span data-testid="pos-fact-total">{rd(total)}</span></div>
              {["efectivo", "combinado"].includes(method) && (Number(paid) || 0) > 0 && <Row l="Cambio" v={rd(change)} />}
              {method === "credito" && <Row l="Balance pendiente" v={rd(balance)} />}
            </div>

            {!online && (
              <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="fact-offline-note">
                <WifiOff size={14} /> Sin conexión: se guardará como provisional y se sincronizará al reconectar.
              </div>
            )}
            <button data-testid="pos-fact-save" onClick={save} disabled={saving}
              className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full py-3.5 text-xs uppercase tracking-[0.15em] hover:bg-rose transition disabled:opacity-60">
              <Receipt size={15} /> {saving ? "Guardando..." : online ? "Guardar factura" : "Guardar sin conexión"}
            </button>
          </div>
        </div>
      </div>
      {camera && <CameraScanner onDetect={handleCode} onClose={() => setCamera(false)} />}
      {quickAdd && <QuickAddCustomer data={quickAdd} onClose={() => setQuickAdd(null)} onSave={saveNewCustomer} />}
      {newProduct && <QuickAddProduct data={newProduct} onClose={() => setNewProduct(null)} onSave={saveNewProduct} />}
      {suggest && <SuggestModal code={suggest.code} matches={suggest.matches} isAdmin={isAdmin} onUse={useSuggested} onCreate={createFromSuggest} onClose={() => setSuggest(null)} />}
    </div>
  );
}

const Row = ({ l, v }) => (
  <div className="flex justify-between text-neutral-500"><span>{l}</span><span>{v}</span></div>
);

// Distancia de edición (Levenshtein) para detectar códigos casi idénticos (mal leídos).
function levenshtein(a, b) {
  const m = a.length, n = b.length;
  if (!m) return n; if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    let cur = [i];
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
    }
    prev = cur;
  }
  return prev[n];
}

// ¿El código escaneado se parece al barcode/código de un producto existente?
function codeResembles(codeRaw, p) {
  const c = String(codeRaw || "").toLowerCase();
  if (c.length < 4) return false;
  const targets = [p.barcode, p.code].filter(Boolean).map((x) => String(x).toLowerCase());
  return targets.some((t) => {
    if (!t || t === c) return false;
    const minLen = Math.min(t.length, c.length);
    let pref = 0;
    while (pref < minLen && t[pref] === c[pref]) pref++;
    if (pref >= Math.max(4, c.length - 2)) return true; // comparten casi todo el prefijo
    if (Math.abs(t.length - c.length) <= 2 && levenshtein(t, c) <= 2) return true; // casi idénticos
    return false;
  });
}

function SuggestModal({ code, matches, isAdmin, onUse, onCreate, onClose }) {
  return (
    <div className="fixed inset-0 z-[95] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100">
          <h2 className="font-serif text-xl">¿Quisiste decir…?</h2>
          <button data-testid="suggest-close" onClick={onClose} className="text-neutral-400 text-xl hover:text-espresso">✕</button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-neutral-500">El código <strong className="text-espresso">{code}</strong> no está registrado, pero se parece a estos productos:</p>
          <div className="space-y-2">
            {matches.map((p) => (
              <button key={p.id} data-testid={`suggest-use-${p.id}`} onClick={() => onUse(p)}
                className="w-full text-left border border-neutral-200 rounded-xl px-3 py-2.5 hover:border-rose hover:bg-rose/5 transition flex items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-medium text-espresso">{p.name}</div>
                  <div className="text-xs text-neutral-400">{[p.barcode, p.code].filter(Boolean).join(" · ")} · stock {p.qty}</div>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${p.qty > 0 ? "bg-emerald-100 text-emerald-700" : "bg-neutral-100 text-neutral-400"}`}>{p.qty > 0 ? "Usar" : "Sin stock"}</span>
              </button>
            ))}
          </div>
          {isAdmin && (
            <button data-testid="suggest-create-new" onClick={() => onCreate(code)}
              className="w-full mt-1 border border-dashed border-neutral-300 rounded-xl px-3 py-2.5 text-sm text-neutral-500 hover:border-espresso hover:text-espresso transition flex items-center justify-center gap-2">
              <Plus size={15} /> No, crear un producto nuevo con el código {code}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function QuickAddProduct({ data, onClose, onSave }) {
  const [d, setD] = useState(data);
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  return (
    <div className="fixed inset-0 z-[95] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100">
          <h2 className="font-serif text-xl">Nuevo producto</h2>
          <button data-testid="qp-close" onClick={onClose} className="text-neutral-400 text-xl hover:text-espresso">✕</button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-center gap-2 text-xs bg-rose/5 border border-rose/20 rounded-lg px-3 py-2 text-espresso">
            <Barcode size={14} className="text-rose" /> Código de barras: <strong>{d.barcode || d.code}</strong>
          </div>
          <div><label className={labelCls}>Nombre del producto</label><input data-testid="qp-name" autoFocus value={d.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Categoría</label>
              <select data-testid="qp-category" value={d.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
                {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Dirigido a</label>
              <select data-testid="qp-gender" value={d.gender} onChange={(e) => set("gender", e.target.value)} className={inputCls}>
                {GENDERS.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className={labelCls}>Precio (RD$)</label><input data-testid="qp-price" type="number" value={d.price} onChange={(e) => set("price", e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>Costo (RD$)</label><input data-testid="qp-cost" type="number" value={d.cost} onChange={(e) => set("cost", e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>Stock inicial</label><input data-testid="qp-qty" type="number" value={d.qty} onChange={(e) => set("qty", e.target.value)} className={inputCls} /></div>
          </div>
          <button data-testid="qp-save" onClick={() => onSave(d)} className="w-full bg-espresso text-cream rounded-full py-3 text-xs uppercase tracking-[0.15em] hover:bg-rose transition">Crear y agregar a la factura</button>
        </div>
      </div>
    </div>
  );
}


function QuickAddCustomer({ data, onClose, onSave }) {
  const [d, setD] = useState(data);
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  return (
    <div className="fixed inset-0 z-[95] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100">
          <h2 className="font-serif text-xl">{d.id ? "Completar ficha del cliente" : "Nuevo cliente"}</h2>
          <button data-testid="quickadd-close" onClick={onClose} className="text-neutral-400 text-xl hover:text-espresso">✕</button>
        </div>
        <div className="p-5 space-y-3">
          <div><label className={labelCls}>Nombre completo</label><input data-testid="quickadd-name" autoFocus value={d.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Teléfono</label><input data-testid="quickadd-phone" value={d.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>WhatsApp</label><input data-testid="quickadd-whatsapp" value={d.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className={inputCls} /></div>
          </div>
          <div><label className={labelCls}>Correo (opcional)</label><input data-testid="quickadd-email" value={d.email} onChange={(e) => set("email", e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Dirección (opcional)</label><textarea data-testid="quickadd-address" value={d.address} onChange={(e) => set("address", e.target.value)} rows={2} placeholder="Dirección de entrega o del cliente..." className={`${inputCls} resize-none`} /></div>
          <div><label className={labelCls}>Cédula o RNC (opcional)</label><input value={d.doc} onChange={(e) => set("doc", e.target.value)} className={inputCls} /></div>
          <button data-testid="quickadd-save" onClick={() => onSave(d)} className="w-full bg-espresso text-cream rounded-full py-3 text-xs uppercase tracking-[0.15em] hover:bg-rose transition">{d.id ? "Guardar ficha y usar en la factura" : "Registrar y usar en la factura"}</button>
        </div>
      </div>
    </div>
  );
}
